<template lang="pug">
.v-field-text.field-text(:class="['field-text--' + field.type, {'field-text--placeholder-state': checkPlaceholderState, 'field-text--sm': field.size === 'sm'}]")
	template(v-if="field.type === 'password'")
		label.field-label(v-if="field.label" :for="uniqueId")
			| {{field.label}}
			span.field-text__star(v-if="field.required") *
		.field-placeholder(v-if="field.placeholder && field.size !== 'sm'")
			| {{field.placeholder}}
			span.field-placeholder__star(v-if="field.required") *
		input.field-input(:class="{'field-input--sm': field.size === 'sm'}"
				:type="isPasswordOpen ? 'text': 'password'"
				:inputmode="field.inputmode"
				:disabled="field.disabled"
				v-model="localValue"
				@focus="handleFocus"
				@blur="handleBlur"
				@input="handleInput"
				@keyup="handleKeyup"
				:id="uniqueId"
				:maxlength="field.maxlength"
				:autocomplete="field.autocomplete || 'off'")
		button.field-text__btn-eye(type="button" @click="isPasswordOpen = !isPasswordOpen")
			v-svg-icon.field-text__btn-eye-icon(v-if="isPasswordOpen" icon="eye")
			v-svg-icon.field-text__btn-eye-icon(v-else icon="eye-closed")
	template(v-else-if="field.type === 'textarea'")
		label.field-label(v-if="field.label" :for="uniqueId")
			| {{field.label}}
			span.field-text__star(v-if="field.required") *
		.field-placeholder(v-if="field.placeholder && field.size !== 'sm'")
			| {{field.placeholder}}
			span.field-placeholder__star(v-if="field.required") *
		textarea.field-input.scrollbar(:style="{'height': field.height}"
				:inputmode="field.inputmode"
				:disabled="field.disabled"
				v-model="localValue"
				@input="handleInput"
				@focus="handleFocus"
				@blur="handleBlur"
				:maxlength="field.maxlength"
				:id="uniqueId")
	template(v-else-if="field.mask")
		label.field-label(v-if="field.label" :for="uniqueId")
			| {{field.label}}
			span.field-text__star(v-if="field.required") *
		.field-placeholder(v-if="field.placeholder && field.size !== 'sm'")
			| {{field.placeholder}}
			span.field-placeholder__star(v-if="field.required") *
		input.field-input(:class="{'field-input--sm': field.size === 'sm'}"
				:type="computedType"
				:inputmode="field.inputmode"
				:disabled="field.disabled"
				:value="localValue"
				:id="uniqueId"
				:placeholder="field.mask.placeholder"
				@focus="handleFocus"
				@blur="handleBlur"
				@accept="onAccept"
				@complete="onComplete"
				:maxlength="field.maxlength"
				:autocomplete="field.autocomplete || 'off'")
	template(v-else)
		label.field-label(v-if="field.label" :for="uniqueId")
			| {{field.label}}
			span.field-text__star(v-if="field.required") *
		.field-placeholder(v-if="field.placeholder && field.size !== 'sm'")
			| {{field.placeholder}}
			span.field-placeholder__star(v-if="field.required") *
		input.field-input(:class="{'field-input--sm': field.size === 'sm'}"
				:type="computedType"
				:inputmode="field.inputmode"
				:disabled="field.disabled"
				v-model="localValue"
				@focus="handleFocus"
				@blur="handleBlur"
				@input="handleInput"
				@keyup="handleKeyup"
				:id="uniqueId"
				:maxlength="field.maxlength"
				:autocomplete="field.autocomplete || 'off'")
	.field-text__note(v-if="field.note") {{field.note}}
</template>

<script>
import uniqueId from '../scripts/helpers/uniqueId.js';

export default {
	props: {
		field: {
			type: Object,
			default: null,
			required: true
		},

		modelValue: {
			type: [String, Number],
			default: null
		}
	},

	emits: [
		'update:model-value'
	],

	data() {
		return {
			hasFocus: false,
			localValue: null,
			savedValue: null,
			unmaskedValue: null,
			uniqueId: uniqueId('field-text_'),
			isPasswordOpen: false
		};
	},

	computed: {
		checkPlaceholderState() {
			if (this.field.size === 'sm') {
				return false;
			}
			return !this.hasFocus && (this.localValue === null || this.localValue === '' || this.localValue.length === 0);
		},

		computedMask() {
			if (this.field.mask.mask === 'Number') {
				return {
					'mask': Number,
					'scale': 3,
					'min': this.field.mask['min'],
					'max': this.field.mask['max'],
					'thousandsSeparator': this.field.mask['thousandsSeparator'],
					'radix': this.field.mask['radix'],
					'mapToRadix': this.field.mask['mapToRadix']
				};
			} else {
				return this.field.mask;
			}
		},

		computedType() {
			if (!this.field.type || this.field.type == 'number') {
				return 'text';
			}
			return this.field.type;
		},

		isFieldNumeric() {
			return !!(this.field.type === 'number' || this.field.inputmode === 'numeric' || this.field.inputmode === 'decimal');
		}
	},

	watch: {
		modelValue: {
			immediate: true,
			handler() {
				if (this.isFieldNumeric && this.modelValue) {
					this.localValue = this.savedValue = this.modelValue.toString().replace('.', ',');
				} else {
					this.localValue = this.savedValue = this.modelValue;
				}
			}
		}
	},

	methods: {
		handleFocus() {
			this.hasFocus = true;
		},

		handleBlur(e) {
			console.log('blur', e);
			this.hasFocus = false;
			if (this.field.mask) {
				this.handleInput();
			} else {
				if (typeof this.field.min === 'number' && this.localValue < this.field.min) {
					this.localValue = this.field.min;
					this.handleInput();
				}
				if (typeof this.field.max === 'number' && this.localValue > this.field.max) {
					this.localValue = this.field.max;
					this.handleInput();
				}
			}
		},

		onAccept(e) {
			const maskRef = e.detail;
			this.localValue = maskRef.value;
		},

		onComplete(e) {
			this.handleInputTel(e);
		},

		handleKeyup() {
			console.log(this.localValue);
			if (this.isFieldNumeric && this.savedValue) {
				this.localValue = this.savedValue.replace('.', ',');
			}
		},

		handleInputTel(e) {
			this.unmaskedValue = e.detail.unmaskedValue;
			this.$emit('update:model-value', this.unmaskedValue);
		},

		handleInput(e) {
			console.log(this.localValue)
			if (this.isFieldNumeric) {
				if (!this.localValue || /^-?(?:\d+)(?:(\.|,)(\d+)?)?$/.test(this.localValue)) {
					this.savedValue = this.localValue;
				} else {
					this.localValue = this.savedValue;
				}
				this.$emit('update:model-value', this.localValue ? parseFloat(this.localValue.toString().replace(',', '.')) : this.localValue);
			} else {
				this.$emit('update:model-value', this.localValue);
			}
		}
	}
};
</script>
